import { isHttpsUrl } from '@/integrations/enablement/is-https-url';
import { openUrlInTauri } from '@/tauri/utils';
import {
  projectsQueryKeys,
  WORK_CHAT_HOME_PATH,
  WORKFLOW_KEYS,
  type RefKind,
  type RefTargetOf,
} from '@heidi/agents/contracts';
import {
  objectPreviewArtifact,
  peekArtifactAtom,
  type Artifact,
  type ObjectPreviewTarget,
  type SessionTokenLabelSource,
} from '@heidi/agents/data';
import {
  COLLECTION_DETAIL_QUERY_KEY,
  DOCUMENT_DOWNLOAD_URL_KEY,
} from '@heidi/evidence/contracts';
import { lettersQueryOptions } from '@heidi/integrations/letters/data';
import { GET_PATIENT_PROFILES } from '@heidi/patients/contracts';
import { useBuildSessionPath } from '@heidi/session';
import { sessionKeys } from '@heidi/session/contracts';
import { clinicalTaskKeys } from '@heidi/tasks/contracts';
import { GET_TEMPLATE_BY_ID } from '@heidi/templates/contracts';
import { useSetAtom } from 'jotai';
import Router from 'next/router';
import { createContext, useContext, type FC, type ReactNode } from 'react';
import type { IntlShape } from 'react-intl';

import { useLetterReference } from './chat-object-letter-reference';
import {
  sourceReference,
  useCollectionReference,
  useDocumentReference,
  useFileReference,
  usePatientReference,
  useProjectReference,
  useRoutineReference,
  useSessionReference,
  useTaskReference,
  useTemplateReference,
  type ObjectReference,
  type TaskReference,
  type TemplateReference,
} from './chat-object-reference-hooks';
import { referenceLabels } from './chat-object-reference-label';

export type ReferenceOpen =
  | { readonly peek: Artifact }
  | { readonly href: string }
  | { readonly external: string }
  | { readonly sessionId: string };

export type ReferenceCache = { getQueryData: (queryKey: readonly unknown[]) => unknown };

export type ReferenceFor<K extends RefKind> = K extends 'template'
  ? TemplateReference
  : K extends 'task'
    ? TaskReference
    : ObjectReference;

export type ReferenceKind<K extends RefKind> = {
  readonly Resolve: FC<{
    target: RefTargetOf<K>;
    labelHint?: string;
    children: (ref: ReferenceFor<K>) => ReactNode;
  }>;
  readonly open?: (
    target: RefTargetOf<K>,
    ref: ReferenceFor<K>
  ) => ReferenceOpen | undefined;
  readonly cachedLabel: (
    cache: ReferenceCache,
    target: RefTargetOf<K>,
    intl: IntlShape
  ) => string | undefined;
};

type Named = Parameters<typeof referenceLabels.document>[0];

const preview = (target: ObjectPreviewTarget): ReferenceOpen => ({
  peek: objectPreviewArtifact(target),
});

export const REFERENCE_KINDS: { readonly [K in RefKind]: ReferenceKind<K> } = {
  patient: {
    Resolve: ({ target, children }) => <>{children(usePatientReference(target.id))}</>,
    open: preview,
    cachedLabel: (cache, target) =>
      referenceLabels.patient(
        cache.getQueryData([
          GET_PATIENT_PROFILES,
          { patientProfileId: target.id },
        ]) as Parameters<typeof referenceLabels.patient>[0]
      ),
  },
  session: {
    Resolve: ({ target, children }) => <>{children(useSessionReference(target.id))}</>,
    // XXX: a session never previews; it opens the Session page, or the host's own session view.
    open: (target) => ({ sessionId: target.id }),
    cachedLabel: (cache, target, intl) =>
      referenceLabels.session(
        cache.getQueryData(sessionKeys.byId(target.id)) as SessionTokenLabelSource,
        intl
      ),
  },
  document: {
    Resolve: ({ target, children }) => (
      <>{children(useDocumentReference(target.id, target.sessionId))}</>
    ),
    open: preview,
    cachedLabel: (cache, target) =>
      referenceLabels.document(
        cache.getQueryData(sessionKeys.documentById(target.sessionId, target.id)) as Named
      ),
  },
  template: {
    Resolve: ({ target, children }) => <>{children(useTemplateReference(target.id))}</>,
    open: (target, ref) => preview({ ...target, formTemplate: ref.formTemplate }),
    // The key `useQueryTemplateById(id, null)` resolves through.
    cachedLabel: (cache, target) =>
      referenceLabels.template(
        cache.getQueryData([GET_TEMPLATE_BY_ID, { templateId: target.id }, null]) as Named
      ),
  },
  routine: {
    Resolve: ({ target, children }) => <>{children(useRoutineReference(target.id))}</>,
    open: preview,
    cachedLabel: (cache, target) =>
      referenceLabels.routine(
        cache.getQueryData(WORKFLOW_KEYS.detail(target.id)) as Named
      ),
  },
  project: {
    Resolve: ({ target, children }) => <>{children(useProjectReference(target.id))}</>,
    open: (target) => ({ href: `${WORK_CHAT_HOME_PATH}/${target.id}` }),
    cachedLabel: (cache, target) =>
      referenceLabels.project(
        cache.getQueryData(projectsQueryKeys.detail(target.id)) as Parameters<
          typeof referenceLabels.project
        >[0]
      ),
  },
  task: {
    Resolve: ({ target, children }) => <>{children(useTaskReference(target.id))}</>,
    // A task with no visit has no side panel to open.
    open: (target, ref) =>
      ref.sessionId ? preview({ ...target, sessionId: ref.sessionId }) : undefined,
    cachedLabel: (cache, target) =>
      referenceLabels.task(
        cache.getQueryData(clinicalTaskKeys.byId(target.id)) as Parameters<
          typeof referenceLabels.task
        >[0]
      ),
  },
  letter: {
    Resolve: ({ target, children }) => (
      <>{children(useLetterReference(target.id, target.sessionId))}</>
    ),
    open: preview,
    cachedLabel: (cache, target) =>
      referenceLabels.letter(
        (
          cache.getQueryData(lettersQueryOptions(target.sessionId).queryKey) as
            | ReadonlyArray<{ id: string } & NonNullable<Named>>
            | undefined
        )?.find((letter) => letter.id === target.id)
      ),
  },
  file: {
    Resolve: ({ target, children }) => <>{children(useFileReference(target.id))}</>,
    open: (target) => preview({ kind: 'library_document', id: target.id }),
    cachedLabel: (cache, target) =>
      referenceLabels.file(
        cache.getQueryData([DOCUMENT_DOWNLOAD_URL_KEY, target.id]) as Parameters<
          typeof referenceLabels.file
        >[0]
      ),
  },
  collection: {
    Resolve: ({ target, children }) => <>{children(useCollectionReference(target.id))}</>,
    open: (target) => ({ href: `/library/collections/${target.id}` }),
    cachedLabel: (cache, target) =>
      referenceLabels.collection(
        cache.getQueryData([COLLECTION_DETAIL_QUERY_KEY, target.id]) as Named
      ),
  },
  source: {
    Resolve: ({ labelHint, children }) => <>{children(sourceReference(labelHint))}</>,
    open: (target) =>
      target.url && isHttpsUrl(target.url) ? { external: target.url } : undefined,
    cachedLabel: () => undefined,
  },
};

// XXX: a host that already shows a session (the in-session Work panel) opens a session
// reference itself, so it can close the drawer that covers that view.
export const SessionReferenceOpenContext = createContext<
  ((sessionId: string) => void) | null
>(null);

export const useOpenReference = (): ((open: ReferenceOpen) => void) => {
  const peek = useSetAtom(peekArtifactAtom);
  const buildSessionPath = useBuildSessionPath();
  const openSessionInHost = useContext(SessionReferenceOpenContext);
  // The Router singleton, not `useRouter()`: prose surfaces mount without a Next router.
  return (open) => {
    if ('peek' in open) peek(open.peek);
    else if ('external' in open)
      void (
        openUrlInTauri(open.external) ??
        window.open(open.external, '_blank', 'noopener,noreferrer')
      );
    else if (!('sessionId' in open)) void Router.push(open.href);
    else if (openSessionInHost) openSessionInHost(open.sessionId);
    else void Router.push(buildSessionPath(open.sessionId));
  };
};
